// ---------------------------------------------------------------------------
// Minimal Google Drive client — no backend needed for this one (unlike
// aemet.ts): a Google OAuth Client ID is not a secret for a public/SPA app
// (Google's own docs say so explicitly), so it can be inlined in the built
// JS like __COMMIT_SHA__ instead of living behind a Cloudflare Pages
// Function. Google Identity Services (GIS) is loaded via <script> at
// runtime — Google's own recommendation, not an npm package, so it stays
// current without a dependency to maintain — and hands back an access token
// directly in the browser (the "token model", meant for apps with no
// backend to keep a refresh token). The Drive API itself is called with
// plain fetch — CORS-open for Bearer-token requests, same as AEMET was.
//
// Scope requested: drive.file — the app only ever sees files/folders it
// creates itself, never the rest of the user's Drive. Keeps this out of
// Google's stricter "restricted scope" verification process, so the
// "Connect" button works from day one instead of waiting on a review.
// ---------------------------------------------------------------------------

import { TranslatableError } from "../i18n/translate.ts";

const GIS_SCRIPT_URL = "https://accounts.google.com/gsi/client";
const DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.file";
const DRIVE_API = "https://www.googleapis.com/drive/v3";
const DRIVE_UPLOAD_API = "https://www.googleapis.com/upload/drive/v3";
const APP_FOLDER_NAME = "EvalHid";
/**
 * Google's own popup-closed detection doesn't always fire its callback
 * (confirmed: closing/backing out of the "Google hasn't verified this app"
 * interstitial can leave `requestAccessToken` hanging forever) — this is the
 * fallback that guarantees the promise always settles, so the UI never gets
 * stuck on "Connecting…".
 *
 * Two very different durations on purpose: `prompt: "consent"` is a real
 * human picking an account and reading a consent screen, which can
 * genuinely take a couple of minutes — but `prompt: ""` (silent, used for
 * `trySilentReconnect` on every app load) is never waiting on a person. If
 * it doesn't resolve almost immediately, it's not going to: browsers
 * routinely block a popup that isn't triggered by a direct user gesture
 * (page load isn't one), so GIS's silent reissuance can end up stuck with
 * no popup and no callback ever coming — confirmed in practice: without a
 * short timeout here, the Settings "Connect" button would sit disabled on
 * "Connecting…" for the full 2 minutes after every single page load.
 */
const ACCESS_TOKEN_TIMEOUT_MS: Record<"" | "consent", number> = {
  "": 8_000,
  consent: 2 * 60 * 1000,
};
/** Custom per-file metadata (see https://developers.google.com/drive/api/guides/properties) used to match a Drive file back to a local `ProjectMeta.id` — the file's own name is cosmetic and can drift after a local rename without breaking that match. */
const PROJECT_ID_PROPERTY = "evalhidProjectId";
/** The project's own content-edit timestamp (`ProjectMeta.lastModified`), stored alongside the file so `driveSyncStore.reconcile` can compare "when was this project last actually edited" across devices. Deliberately NOT the same clock as Drive's own `modifiedTime` (the file's upload time) — that only says when this device last pushed a copy, not when the content itself changed, and comparing it against a local project's (possibly much older) `lastModified` would flag a false conflict on every upload of an untouched project. */
const LAST_MODIFIED_PROPERTY = "evalhidLastModified";

interface TokenResponse {
  access_token?: string;
  /** Seconds until this token stops working — Google's own OAuth token responses always include it; defaults to a conservative 30 minutes on the off chance a real response ever omits it, well under the real ~1h Drive tokens actually last. */
  expires_in?: number;
  error?: string;
  error_description?: string;
}

interface AccessTokenResult {
  accessToken: string;
  /** Epoch ms — `Date.now()` at response time plus the response's own `expires_in`, so driveSyncStore.ts can cache this and skip the whole reconnect dance (silent or not) on a later page load until it actually expires. */
  expiresAt: number;
}

interface TokenClient {
  requestAccessToken(opts: { prompt: string }): void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient(config: {
            client_id: string;
            scope: string;
            callback: (response: TokenResponse) => void;
          }): TokenClient;
          revoke(accessToken: string, callback?: () => void): void;
        };
      };
    };
  }
}

let gisLoadPromise: Promise<void> | null = null;

/** Injects the GIS `<script>` once (idempotent — safe to call every time `requestAccessToken` runs) and resolves once `window.google.accounts.oauth2` is ready. */
function loadGoogleIdentityServices(): Promise<void> {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  if (gisLoadPromise) return gisLoadPromise;
  gisLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = GIS_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (window.google?.accounts?.oauth2) resolve();
      else
        reject(
          new Error("Google Identity Services loaded but window.google.accounts.oauth2 is missing.")
        );
    };
    script.onerror = () => reject(new Error("Failed to load the Google Identity Services script."));
    document.head.appendChild(script);
  });
  return gisLoadPromise;
}

/**
 * `prompt: ""` — silent: only resolves if the browser still has an active
 * Google session AND the user already granted `drive.file` before (used to
 * reconnect on app startup — see driveSyncStore.ts's `trySilentReconnect`).
 * Rejects (doesn't hang) if silent auth isn't possible.
 * `prompt: "consent"` — always shows Google's real consent screen (first
 * connect, or after the user revoked access some other way).
 */
export function requestAccessToken(
  clientId: string,
  opts: { prompt: "" | "consent" }
): Promise<AccessTokenResult> {
  return loadGoogleIdentityServices().then(
    () =>
      new Promise<AccessTokenResult>((resolve, reject) => {
        let settled = false;
        // See ACCESS_TOKEN_TIMEOUT_MS's own comment — Google's callback
        // isn't guaranteed to fire when the popup is closed/abandoned
        // without completing, so this is what actually guarantees the
        // promise settles either way.
        const timeoutId = window.setTimeout(() => {
          if (settled) return;
          settled = true;
          reject(new TranslatableError("driveSync.signInTimeout"));
        }, ACCESS_TOKEN_TIMEOUT_MS[opts.prompt]);
        const client = window.google!.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: DRIVE_SCOPE,
          callback: (response) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeoutId);
            if (response.access_token) {
              resolve({
                accessToken: response.access_token,
                expiresAt: Date.now() + (response.expires_in ?? 30 * 60) * 1000,
              });
            } else
              reject(
                response.error_description || response.error
                  ? new Error(response.error_description ?? response.error)
                  : new TranslatableError("driveSync.noAccessToken")
              );
          },
        });
        client.requestAccessToken({ prompt: opts.prompt });
      })
  );
}

/** Best-effort — if GIS never loaded (e.g. offline), there's nothing to revoke client-side; the token simply expires on its own within the hour. */
export function revokeAccessToken(token: string): void {
  window.google?.accounts?.oauth2.revoke(token);
}

interface DriveFile {
  id: string;
  name: string;
}

async function authFetch(token: string, url: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(url, {
    ...init,
    headers: { ...init?.headers, Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    throw new Error(`Google Drive API error ${res.status}: ${body || res.statusText}`);
  }
  return res;
}

function driveFetch(token: string, path: string, init?: RequestInit): Promise<Response> {
  return authFetch(token, `${DRIVE_API}${path}`, init);
}

/** Finds the app's own "EvalHid" folder (`drive.file` scope only ever lists folders this app itself created) or creates it — one round-trip either way. */
export async function findOrCreateAppFolder(token: string): Promise<string> {
  const q = encodeURIComponent(
    `mimeType='application/vnd.google-apps.folder' and name='${APP_FOLDER_NAME}' and trashed=false`
  );
  const listRes = await driveFetch(token, `/files?q=${q}&fields=files(id,name)`);
  const { files } = (await listRes.json()) as { files: DriveFile[] };
  if (files.length > 0) return files[0].id;

  const createRes = await driveFetch(token, "/files?fields=id", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name: APP_FOLDER_NAME, mimeType: "application/vnd.google-apps.folder" }),
  });
  const created = (await createRes.json()) as DriveFile;
  return created.id;
}

interface DriveProjectFile {
  id: string;
  name: string;
  /** The project's own content-edit clock (see LAST_MODIFIED_PROPERTY) — falls back to Drive's own `modifiedTime` only for a file that predates this property existing, so reconcile still has *something* to compare against instead of crashing on `undefined`. */
  lastModified: number;
  /** Undefined if some other means put a file in the folder without our custom property — shouldn't normally happen, but callers shouldn't crash on it. */
  projectId?: string;
}

/** Every non-trashed file directly inside the app's folder — used to reconcile against the local project list (see driveSyncStore.ts's `reconcile`, a later phase). */
export async function listProjectFiles(
  token: string,
  folderId: string
): Promise<DriveProjectFile[]> {
  const q = encodeURIComponent(`'${folderId}' in parents and trashed=false`);
  const res = await driveFetch(
    token,
    `/files?q=${q}&fields=files(id,name,modifiedTime,appProperties)`
  );
  const { files } = (await res.json()) as {
    files: {
      id: string;
      name: string;
      modifiedTime: string;
      appProperties?: Record<string, string>;
    }[];
  };
  return files.map((f) => {
    const storedLastModified = f.appProperties?.[LAST_MODIFIED_PROPERTY];
    return {
      id: f.id,
      name: f.name,
      lastModified: storedLastModified ? Number(storedLastModified) : Date.parse(f.modifiedTime),
      projectId: f.appProperties?.[PROJECT_ID_PROPERTY],
    };
  });
}

function buildMultipartBody(boundary: string, metadata: unknown, content: Blob): Blob {
  const header =
    `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify(metadata)}\r\n` +
    `--${boundary}\r\nContent-Type: ${content.type || "application/octet-stream"}\r\n\r\n`;
  const footer = `\r\n--${boundary}--`;
  return new Blob([header, content, footer]);
}

/**
 * Creates a new file in `folderId` (no `existingFileId`) or replaces the
 * content of an already-uploaded one (with it) — same `appProperties`
 * either way, so a later `listProjectFiles` can match it back to
 * `projectId`. Multipart upload (metadata + content in one request) — Drive
 * API v3's simplest upload type, fine for project files (a real one is a
 * few MB at most, well under the multipart size ceiling).
 */
export async function uploadProjectFile(
  token: string,
  folderId: string,
  fileName: string,
  content: Blob,
  projectId: string,
  lastModified: number,
  existingFileId?: string
): Promise<{ id: string; modifiedTime: string }> {
  const appProperties = {
    [PROJECT_ID_PROPERTY]: projectId,
    [LAST_MODIFIED_PROPERTY]: String(lastModified),
  };
  const metadata = existingFileId
    ? { name: fileName, appProperties }
    : { name: fileName, parents: [folderId], appProperties };
  const boundary = `evalhid-${Math.random().toString(36).slice(2)}`;
  const body = buildMultipartBody(boundary, metadata, content);
  const path = existingFileId
    ? `/files/${existingFileId}?uploadType=multipart&fields=id,modifiedTime`
    : `/files?uploadType=multipart&fields=id,modifiedTime`;
  const res = await authFetch(token, `${DRIVE_UPLOAD_API}${path}`, {
    method: existingFileId ? "PATCH" : "POST",
    headers: { "Content-Type": `multipart/related; boundary=${boundary}` },
    body,
  });
  return (await res.json()) as { id: string; modifiedTime: string };
}

export async function downloadProjectFile(token: string, fileId: string): Promise<Blob> {
  const res = await driveFetch(token, `/files/${fileId}?alt=media`);
  return res.blob();
}

export async function deleteProjectFile(token: string, fileId: string): Promise<void> {
  await driveFetch(token, `/files/${fileId}`, { method: "DELETE" });
}
